// The owner's setup of what a saved App function may change in the App's saved data (selected App controls).
// Namespace `app-function-write-setup`; owner: T2-UI2; all 24 languages from DES2, verbatim
// (org/work/DES2/handoff/app-function-write-setup-24.json). No placeholders.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  heading: 'Allow this function to change App data',
  intro:
    'Choose the function, who may use it and the changes it may make. Existing App access still applies.',
  function: 'Function',
  who: 'Who may use it',
  owner: 'Only the App owner',
  members: 'People with access to this App',
  collection: 'Saved data',
  effects: 'Allowed changes',
  insert: 'Add rows',
  update: 'Edit rows',
  delete: 'Remove rows',
  allow: 'Allow changes',
  checking: 'Checking…',
  check: 'Check again',
  allowed: 'These changes are allowed.',
  denied: 'You can’t allow these changes.',
  changed: 'The App changed. Reopen its current version before changing access.',
  invalid: 'Review the function and allowed changes.',
  unknown:
    'We couldn’t confirm whether these changes were allowed. Check again to confirm the same request.',
  storage: 'We couldn’t keep this request in this tab. Reload before trying again.',
  another: 'Set up another function',
};
export type AppFunctionWriteSetupCopyKey = keyof typeof en;
type Copy = Record<AppFunctionWriteSetupCopyKey, string>;
const bg: Copy = {
  heading: 'Разрешаване на тази функция да променя данните на приложението',
  intro:
    'Изберете функцията, кой може да я използва и какви промени може да прави. Съществуващият достъп до приложението продължава да важи.',
  function: 'Функция',
  who: 'Кой може да я използва',
  owner: 'Само собственикът на приложението',
  members: 'Хората с достъп до това приложение',
  collection: 'Запазени данни',
  effects: 'Разрешени промени',
  insert: 'Добавяне на редове',
  update: 'Редактиране на редове',
  delete: 'Премахване на редове',
  allow: 'Разрешаване на промените',
  checking: 'Проверява се…',
  check: 'Проверка отново',
  allowed: 'Тези промени са разрешени.',
  denied: 'Не можете да разрешите тези промени.',
  changed:
    'Приложението се промени. Отворете отново текущата му версия, преди да промените достъпа.',
  invalid: 'Прегледайте функцията и разрешените промени.',
  unknown:
    'Не успяхме да потвърдим дали тези промени са разрешени. Проверете отново, за да потвърдите същата заявка.',
  storage: 'Не успяхме да запазим тази заявка в този раздел. Презаредете, преди да опитате отново.',
  another: 'Настройване на друга функция',
};
const cs: Copy = {
  heading: 'Povolit této funkci měnit data aplikace',
  intro:
    'Vyberte funkci, kdo ji smí používat a jaké změny smí provádět. Stávající přístup k aplikaci platí i nadále.',
  function: 'Funkce',
  who: 'Kdo ji smí používat',
  owner: 'Pouze vlastník aplikace',
  members: 'Lidé s přístupem k této aplikaci',
  collection: 'Uložená data',
  effects: 'Povolené změny',
  insert: 'Přidávat řádky',
  update: 'Upravovat řádky',
  delete: 'Odebírat řádky',
  allow: 'Povolit změny',
  checking: 'Kontrola…',
  check: 'Zkontrolovat znovu',
  allowed: 'Tyto změny jsou povolené.',
  denied: 'Tyto změny nemůžete povolit.',
  changed: 'Aplikace se změnila. Před změnou přístupu znovu otevřete její aktuální verzi.',
  invalid: 'Zkontrolujte funkci a povolené změny.',
  unknown:
    'Nepodařilo se ověřit, zda byly tyto změny povoleny. Zkontrolujte to znovu a potvrďte stejný požadavek.',
  storage:
    'Tento požadavek se nepodařilo uchovat v této kartě. Před dalším pokusem stránku znovu načtěte.',
  another: 'Nastavit další funkci',
};
const da: Copy = {
  heading: 'Tillad denne funktion at ændre appdata',
  intro:
    'Vælg funktionen, hvem der må bruge den, og hvilke ændringer den må foretage. Eksisterende adgang til appen gælder stadig.',
  function: 'Funktion',
  who: 'Hvem der må bruge den',
  owner: 'Kun appens ejer',
  members: 'Personer med adgang til denne app',
  collection: 'Gemte data',
  effects: 'Tilladte ændringer',
  insert: 'Tilføje rækker',
  update: 'Redigere rækker',
  delete: 'Fjerne rækker',
  allow: 'Tillad ændringer',
  checking: 'Kontrollerer…',
  check: 'Tjek igen',
  allowed: 'Disse ændringer er tilladt.',
  denied: 'Du kan ikke tillade disse ændringer.',
  changed: 'Appen er ændret. Åbn dens aktuelle version igen, før du ændrer adgangen.',
  invalid: 'Gennemgå funktionen og de tilladte ændringer.',
  unknown:
    'Vi kunne ikke bekræfte, om ændringerne blev tilladt. Tjek igen for at bekræfte den samme anmodning.',
  storage: 'Vi kunne ikke beholde anmodningen i denne fane. Genindlæs, før du prøver igen.',
  another: 'Konfigurer en funktion mere',
};
const de: Copy = {
  heading: 'Dieser Funktion erlauben, App-Daten zu ändern',
  intro:
    'Wählen Sie die Funktion, wer sie verwenden darf und welche Änderungen sie vornehmen darf. Bestehende Zugriffsrechte für die App gelten weiterhin.',
  function: 'Funktion',
  who: 'Wer sie verwenden darf',
  owner: 'Nur der App-Inhaber',
  members: 'Personen mit Zugriff auf diese App',
  collection: 'Gespeicherte Daten',
  effects: 'Erlaubte Änderungen',
  insert: 'Zeilen hinzufügen',
  update: 'Zeilen bearbeiten',
  delete: 'Zeilen entfernen',
  allow: 'Änderungen erlauben',
  checking: 'Wird geprüft…',
  check: 'Erneut prüfen',
  allowed: 'Diese Änderungen sind erlaubt.',
  denied: 'Sie können diese Änderungen nicht erlauben.',
  changed:
    'Die App hat sich geändert. Öffnen Sie ihre aktuelle Version erneut, bevor Sie den Zugriff ändern.',
  invalid: 'Prüfen Sie die Funktion und die erlaubten Änderungen.',
  unknown:
    'Wir konnten nicht bestätigen, ob diese Änderungen erlaubt wurden. Prüfen Sie erneut, um dieselbe Anfrage zu bestätigen.',
  storage:
    'Wir konnten diese Anfrage in diesem Tab nicht behalten. Laden Sie neu, bevor Sie es erneut versuchen.',
  another: 'Weitere Funktion einrichten',
};
const el: Copy = {
  heading: 'Να επιτρέπεται σε αυτή τη συνάρτηση να αλλάζει δεδομένα της εφαρμογής',
  intro:
    'Επιλέξτε τη συνάρτηση, ποιος μπορεί να τη χρησιμοποιεί και τις αλλαγές που μπορεί να κάνει. Η υπάρχουσα πρόσβαση στην εφαρμογή εξακολουθεί να ισχύει.',
  function: 'Συνάρτηση',
  who: 'Ποιος μπορεί να τη χρησιμοποιεί',
  owner: 'Μόνο ο κάτοχος της εφαρμογής',
  members: 'Άτομα με πρόσβαση σε αυτή την εφαρμογή',
  collection: 'Αποθηκευμένα δεδομένα',
  effects: 'Επιτρεπόμενες αλλαγές',
  insert: 'Προσθήκη γραμμών',
  update: 'Επεξεργασία γραμμών',
  delete: 'Αφαίρεση γραμμών',
  allow: 'Να επιτρέπονται οι αλλαγές',
  checking: 'Έλεγχος…',
  check: 'Έλεγχος ξανά',
  allowed: 'Αυτές οι αλλαγές επιτρέπονται.',
  denied: 'Δεν μπορείτε να επιτρέψετε αυτές τις αλλαγές.',
  changed: 'Η εφαρμογή άλλαξε. Ανοίξτε ξανά την τρέχουσα έκδοσή της πριν αλλάξετε την πρόσβαση.',
  invalid: 'Ελέγξτε τη συνάρτηση και τις επιτρεπόμενες αλλαγές.',
  unknown:
    'Δεν μπορέσαμε να επιβεβαιώσουμε αν επιτράπηκαν αυτές οι αλλαγές. Ελέγξτε ξανά για να επιβεβαιώσετε το ίδιο αίτημα.',
  storage:
    'Δεν μπορέσαμε να διατηρήσουμε αυτό το αίτημα σε αυτή την καρτέλα. Φορτώστε ξανά τη σελίδα πριν δοκιμάσετε ξανά.',
  another: 'Ρύθμιση άλλης συνάρτησης',
};
const es: Copy = {
  heading: 'Permitir que esta función cambie los datos de la app',
  intro:
    'Elige la función, quién puede usarla y los cambios que puede hacer. El acceso actual a la app sigue aplicándose.',
  function: 'Función',
  who: 'Quién puede usarla',
  owner: 'Solo el propietario de la app',
  members: 'Personas con acceso a esta app',
  collection: 'Datos guardados',
  effects: 'Cambios permitidos',
  insert: 'Añadir filas',
  update: 'Editar filas',
  delete: 'Eliminar filas',
  allow: 'Permitir cambios',
  checking: 'Comprobando…',
  check: 'Comprobar de nuevo',
  allowed: 'Estos cambios están permitidos.',
  denied: 'No puedes permitir estos cambios.',
  changed: 'La app ha cambiado. Vuelve a abrir su versión actual antes de cambiar el acceso.',
  invalid: 'Revisa la función y los cambios permitidos.',
  unknown:
    'No pudimos confirmar si se permitieron estos cambios. Vuelve a comprobarlo para confirmar la misma solicitud.',
  storage:
    'No pudimos conservar esta solicitud en esta pestaña. Recarga antes de volver a intentarlo.',
  another: 'Configurar otra función',
};
const et: Copy = {
  heading: 'Luba sellel funktsioonil rakenduse andmeid muuta',
  intro:
    'Vali funktsioon, kes tohib seda kasutada ja milliseid muudatusi see tohib teha. Rakenduse olemasolevad juurdepääsuõigused kehtivad endiselt.',
  function: 'Funktsioon',
  who: 'Kes tohib seda kasutada',
  owner: 'Ainult rakenduse omanik',
  members: 'Inimesed, kellel on juurdepääs sellele rakendusele',
  collection: 'Salvestatud andmed',
  effects: 'Lubatud muudatused',
  insert: 'Ridade lisamine',
  update: 'Ridade muutmine',
  delete: 'Ridade eemaldamine',
  allow: 'Luba muudatused',
  checking: 'Kontrollimine…',
  check: 'Kontrolli uuesti',
  allowed: 'Need muudatused on lubatud.',
  denied: 'Sa ei saa neid muudatusi lubada.',
  changed: 'Rakendus on muutunud. Ava selle praegune versioon uuesti, enne kui juurdepääsu muudad.',
  invalid: 'Vaata üle funktsioon ja lubatud muudatused.',
  unknown:
    'Me ei saanud kinnitada, kas need muudatused lubati. Kontrolli uuesti, et sama päring kinnitada.',
  storage:
    'Me ei saanud seda päringut selles vahekaardis säilitada. Laadi leht uuesti, enne kui uuesti proovid.',
  another: 'Seadista veel üks funktsioon',
};
const fi: Copy = {
  heading: 'Salli tämän funktion muuttaa sovelluksen tietoja',
  intro:
    'Valitse funktio, kuka saa käyttää sitä ja mitä muutoksia se saa tehdä. Sovelluksen nykyiset käyttöoikeudet ovat edelleen voimassa.',
  function: 'Funktio',
  who: 'Kuka saa käyttää sitä',
  owner: 'Vain sovelluksen omistaja',
  members: 'Henkilöt, joilla on pääsy tähän sovellukseen',
  collection: 'Tallennetut tiedot',
  effects: 'Sallitut muutokset',
  insert: 'Rivien lisääminen',
  update: 'Rivien muokkaaminen',
  delete: 'Rivien poistaminen',
  allow: 'Salli muutokset',
  checking: 'Tarkistetaan…',
  check: 'Tarkista uudelleen',
  allowed: 'Nämä muutokset on sallittu.',
  denied: 'Et voi sallia näitä muutoksia.',
  changed:
    'Sovellus on muuttunut. Avaa sen nykyinen versio uudelleen ennen kuin muutat käyttöoikeuksia.',
  invalid: 'Tarkista funktio ja sallitut muutokset.',
  unknown:
    'Emme voineet vahvistaa, sallittiinko nämä muutokset. Tarkista uudelleen, niin sama pyyntö vahvistetaan.',
  storage:
    'Emme voineet säilyttää tätä pyyntöä tällä välilehdellä. Lataa sivu uudelleen ennen kuin yrität uudestaan.',
  another: 'Määritä toinen funktio',
};
const fr: Copy = {
  heading: 'Autoriser cette fonction à modifier les données de l’app',
  intro:
    'Choisissez la fonction, qui peut l’utiliser et les modifications qu’elle peut apporter. Les accès existants à l’app restent valables.',
  function: 'Fonction',
  who: 'Qui peut l’utiliser',
  owner: 'Seul le propriétaire de l’app',
  members: 'Personnes ayant accès à cette app',
  collection: 'Données enregistrées',
  effects: 'Modifications autorisées',
  insert: 'Ajouter des lignes',
  update: 'Modifier des lignes',
  delete: 'Supprimer des lignes',
  allow: 'Autoriser les modifications',
  checking: 'Vérification en cours…',
  check: 'Vérifier à nouveau',
  allowed: 'Ces modifications sont autorisées.',
  denied: 'Vous ne pouvez pas autoriser ces modifications.',
  changed: 'L’app a changé. Rouvrez sa version actuelle avant de modifier l’accès.',
  invalid: 'Vérifiez la fonction et les modifications autorisées.',
  unknown:
    'Nous n’avons pas pu confirmer si ces modifications ont été autorisées. Vérifiez à nouveau pour confirmer la même demande.',
  storage:
    'Nous n’avons pas pu conserver cette demande dans cet onglet. Rechargez avant de réessayer.',
  another: 'Configurer une autre fonction',
};
const ga: Copy = {
  heading: 'Lig don fheidhm seo sonraí na haipe a athrú',
  intro:
    'Roghnaigh an fheidhm, cé atá in ann í a úsáid agus na hathruithe atá ceadaithe di. Tá an rochtain atá ar an aip cheana fós i bhfeidhm.',
  function: 'Feidhm',
  who: 'Cé atá in ann í a úsáid',
  owner: 'Úinéir na haipe amháin',
  members: 'Daoine a bhfuil rochtain acu ar an aip seo',
  collection: 'Sonraí sábháilte',
  effects: 'Athruithe ceadaithe',
  insert: 'Rónna a chur leis',
  update: 'Rónna a chur in eagar',
  delete: 'Rónna a bhaint',
  allow: 'Ceadaigh athruithe',
  checking: 'Ag seiceáil…',
  check: 'Seiceáil arís',
  allowed: 'Tá na hathruithe seo ceadaithe.',
  denied: 'Ní féidir leat na hathruithe seo a cheadú.',
  changed: 'D’athraigh an aip. Oscail an leagan reatha di arís sula n-athraíonn tú an rochtain.',
  invalid: 'Athbhreithnigh an fheidhm agus na hathruithe ceadaithe.',
  unknown:
    'Níorbh fhéidir linn a dheimhniú ar ceadaíodh na hathruithe seo. Seiceáil arís chun an t-iarratas céanna a dheimhniú.',
  storage:
    'Níorbh fhéidir linn an t-iarratas seo a choinneáil sa chluaisín seo. Athluchtaigh sula ndéanann tú iarracht eile.',
  another: 'Socraigh feidhm eile',
};
const hr: Copy = {
  heading: 'Dopusti ovoj funkciji da mijenja podatke aplikacije',
  intro:
    'Odaberite funkciju, tko je smije koristiti i koje promjene smije raditi. Postojeći pristup aplikaciji i dalje vrijedi.',
  function: 'Funkcija',
  who: 'Tko je smije koristiti',
  owner: 'Samo vlasnik aplikacije',
  members: 'Osobe s pristupom ovoj aplikaciji',
  collection: 'Spremljeni podaci',
  effects: 'Dopuštene promjene',
  insert: 'Dodavanje redaka',
  update: 'Uređivanje redaka',
  delete: 'Uklanjanje redaka',
  allow: 'Dopusti promjene',
  checking: 'Provjera…',
  check: 'Provjeri ponovno',
  allowed: 'Ove su promjene dopuštene.',
  denied: 'Ne možete dopustiti ove promjene.',
  changed:
    'Aplikacija se promijenila. Ponovno otvorite njezinu trenutačnu verziju prije promjene pristupa.',
  invalid: 'Provjerite funkciju i dopuštene promjene.',
  unknown:
    'Nismo mogli potvrditi jesu li ove promjene dopuštene. Provjerite ponovno da biste potvrdili isti zahtjev.',
  storage:
    'Nismo mogli zadržati ovaj zahtjev u ovoj kartici. Ponovno učitajte prije novog pokušaja.',
  another: 'Postavi još jednu funkciju',
};
const hu: Copy = {
  heading: 'Alkalmazásadatok módosításának engedélyezése ennek a funkciónak',
  intro:
    'Válassza ki a funkciót, azt, hogy ki használhatja, és hogy milyen módosításokat végezhet. Az alkalmazáshoz meglévő hozzáférések továbbra is érvényesek.',
  function: 'Funkció',
  who: 'Ki használhatja',
  owner: 'Csak az alkalmazás tulajdonosa',
  members: 'Akik hozzáférnek ehhez az alkalmazáshoz',
  collection: 'Mentett adatok',
  effects: 'Engedélyezett módosítások',
  insert: 'Sorok hozzáadása',
  update: 'Sorok szerkesztése',
  delete: 'Sorok eltávolítása',
  allow: 'Módosítások engedélyezése',
  checking: 'Ellenőrzés…',
  check: 'Ellenőrzés újra',
  allowed: 'Ezek a módosítások engedélyezettek.',
  denied: 'Ezeket a módosításokat nem engedélyezheti.',
  changed:
    'Az alkalmazás megváltozott. A hozzáférés módosítása előtt nyissa meg újra az aktuális verzióját.',
  invalid: 'Ellenőrizze a funkciót és az engedélyezett módosításokat.',
  unknown:
    'Nem tudtuk megerősíteni, hogy ezek a módosítások engedélyezve lettek-e. Ellenőrizze újra ugyanannak a kérésnek a megerősítéséhez.',
  storage:
    'Nem tudtuk megtartani ezt a kérést ezen a lapon. Töltse újra az oldalt, mielőtt újra próbálkozik.',
  another: 'Másik funkció beállítása',
};
const it: Copy = {
  heading: 'Consenti a questa funzione di modificare i dati dell’App',
  intro:
    'Scegli la funzione, chi può usarla e le modifiche che può apportare. Gli accessi esistenti all’App restano validi.',
  function: 'Funzione',
  who: 'Chi può usarla',
  owner: 'Solo il proprietario dell’App',
  members: 'Persone con accesso a questa App',
  collection: 'Dati salvati',
  effects: 'Modifiche consentite',
  insert: 'Aggiungere righe',
  update: 'Modificare righe',
  delete: 'Rimuovere righe',
  allow: 'Consenti modifiche',
  checking: 'Controllo in corso…',
  check: 'Controlla di nuovo',
  allowed: 'Queste modifiche sono consentite.',
  denied: 'Non puoi consentire queste modifiche.',
  changed: 'L’App è cambiata. Riapri la sua versione attuale prima di modificare l’accesso.',
  invalid: 'Controlla la funzione e le modifiche consentite.',
  unknown:
    'Non siamo riusciti a confermare se queste modifiche sono state consentite. Controlla di nuovo per confermare la stessa richiesta.',
  storage:
    'Non siamo riusciti a conservare questa richiesta in questa scheda. Ricarica prima di riprovare.',
  another: 'Configura un’altra funzione',
};
const lt: Copy = {
  heading: 'Leisti šiai funkcijai keisti programėlės duomenis',
  intro:
    'Pasirinkite funkciją, kas gali ja naudotis ir kokius pakeitimus ji gali atlikti. Esama prieiga prie programėlės vis dar galioja.',
  function: 'Funkcija',
  who: 'Kas gali ja naudotis',
  owner: 'Tik programėlės savininkas',
  members: 'Žmonės, turintys prieigą prie šios programėlės',
  collection: 'Išsaugoti duomenys',
  effects: 'Leidžiami pakeitimai',
  insert: 'Pridėti eilutes',
  update: 'Redaguoti eilutes',
  delete: 'Šalinti eilutes',
  allow: 'Leisti pakeitimus',
  checking: 'Tikrinama…',
  check: 'Tikrinti dar kartą',
  allowed: 'Šie pakeitimai leidžiami.',
  denied: 'Negalite leisti šių pakeitimų.',
  changed:
    'Programėlė pasikeitė. Prieš keisdami prieigą iš naujo atidarykite dabartinę jos versiją.',
  invalid: 'Peržiūrėkite funkciją ir leidžiamus pakeitimus.',
  unknown:
    'Nepavyko patvirtinti, ar šie pakeitimai buvo leisti. Patikrinkite dar kartą, kad patvirtintumėte tą pačią užklausą.',
  storage:
    'Nepavyko išlaikyti šios užklausos šiame skirtuke. Prieš bandydami dar kartą įkelkite puslapį iš naujo.',
  another: 'Nustatyti kitą funkciją',
};
const lv: Copy = {
  heading: 'Atļaut šai funkcijai mainīt lietotnes datus',
  intro:
    'Izvēlieties funkciju, kas to drīkst izmantot un kādas izmaiņas tā drīkst veikt. Esošā piekļuve lietotnei joprojām ir spēkā.',
  function: 'Funkcija',
  who: 'Kas to drīkst izmantot',
  owner: 'Tikai lietotnes īpašnieks',
  members: 'Cilvēki ar piekļuvi šai lietotnei',
  collection: 'Saglabātie dati',
  effects: 'Atļautās izmaiņas',
  insert: 'Pievienot rindas',
  update: 'Rediģēt rindas',
  delete: 'Noņemt rindas',
  allow: 'Atļaut izmaiņas',
  checking: 'Notiek pārbaude…',
  check: 'Pārbaudīt vēlreiz',
  allowed: 'Šīs izmaiņas ir atļautas.',
  denied: 'Jūs nevarat atļaut šīs izmaiņas.',
  changed:
    'Lietotne ir mainījusies. Pirms piekļuves maiņas atkārtoti atveriet tās pašreizējo versiju.',
  invalid: 'Pārskatiet funkciju un atļautās izmaiņas.',
  unknown:
    'Mēs nevarējām apstiprināt, vai šīs izmaiņas tika atļautas. Pārbaudiet vēlreiz, lai apstiprinātu to pašu pieprasījumu.',
  storage:
    'Mēs nevarējām saglabāt šo pieprasījumu šajā cilnē. Pirms mēģināt vēlreiz, pārlādējiet lapu.',
  another: 'Iestatīt vēl vienu funkciju',
};
const mt: Copy = {
  heading: 'Ħalli din il-funzjoni tibdel id-dejta tal-App',
  intro:
    'Agħżel il-funzjoni, min jista’ jużaha u l-bidliet li tista’ tagħmel. L-aċċess eżistenti għall-App jibqa’ japplika.',
  function: 'Funzjoni',
  who: 'Min jista’ jużaha',
  owner: 'Is-sid tal-App biss',
  members: 'Nies b’aċċess għal din l-App',
  collection: 'Dejta salvata',
  effects: 'Bidliet permessi',
  insert: 'Żid ringieli',
  update: 'Editja ringieli',
  delete: 'Neħħi ringieli',
  allow: 'Ippermetti l-bidliet',
  checking: 'Qed jikkontrolla…',
  check: 'Iċċekkja mill-ġdid',
  allowed: 'Dawn il-bidliet huma permessi.',
  denied: 'Ma tistax tippermetti dawn il-bidliet.',
  changed: 'L-App inbidlet. Erġa’ iftaħ il-verżjoni attwali tagħha qabel ma tibdel l-aċċess.',
  invalid: 'Irrevedi l-funzjoni u l-bidliet permessi.',
  unknown:
    'Ma stajniex nikkonfermaw jekk dawn il-bidliet ġewx permessi. Iċċekkja mill-ġdid biex tikkonferma l-istess talba.',
  storage:
    'Ma stajniex inżommu din it-talba f’din it-tab. Erġa’ tella’ l-paġna qabel ma terġa’ tipprova.',
  another: 'Issettja funzjoni oħra',
};
const nl: Copy = {
  heading: 'Deze functie toestaan app-gegevens te wijzigen',
  intro:
    'Kies de functie, wie die mag gebruiken en welke wijzigingen die mag aanbrengen. Bestaande toegang tot de app blijft gelden.',
  function: 'Functie',
  who: 'Wie die mag gebruiken',
  owner: 'Alleen de eigenaar van de app',
  members: 'Mensen met toegang tot deze app',
  collection: 'Opgeslagen gegevens',
  effects: 'Toegestane wijzigingen',
  insert: 'Rijen toevoegen',
  update: 'Rijen bewerken',
  delete: 'Rijen verwijderen',
  allow: 'Wijzigingen toestaan',
  checking: 'Controleren…',
  check: 'Opnieuw controleren',
  allowed: 'Deze wijzigingen zijn toegestaan.',
  denied: 'Je kunt deze wijzigingen niet toestaan.',
  changed: 'De app is gewijzigd. Open de huidige versie opnieuw voordat je de toegang wijzigt.',
  invalid: 'Controleer de functie en de toegestane wijzigingen.',
  unknown:
    'We konden niet bevestigen of deze wijzigingen zijn toegestaan. Controleer opnieuw om hetzelfde verzoek te bevestigen.',
  storage:
    'We konden dit verzoek niet in dit tabblad bewaren. Laad opnieuw voordat je het nog eens probeert.',
  another: 'Nog een functie instellen',
};
const pl: Copy = {
  heading: 'Pozwól tej funkcji zmieniać dane aplikacji',
  intro:
    'Wybierz funkcję, kto może jej używać i jakie zmiany może wprowadzać. Dotychczasowy dostęp do aplikacji nadal obowiązuje.',
  function: 'Funkcja',
  who: 'Kto może jej używać',
  owner: 'Tylko właściciel aplikacji',
  members: 'Osoby z dostępem do tej aplikacji',
  collection: 'Zapisane dane',
  effects: 'Dozwolone zmiany',
  insert: 'Dodawanie wierszy',
  update: 'Edytowanie wierszy',
  delete: 'Usuwanie wierszy',
  allow: 'Zezwól na zmiany',
  checking: 'Sprawdzanie…',
  check: 'Sprawdź ponownie',
  allowed: 'Te zmiany są dozwolone.',
  denied: 'Nie możesz zezwolić na te zmiany.',
  changed: 'Aplikacja się zmieniła. Przed zmianą dostępu otwórz ponownie jej bieżącą wersję.',
  invalid: 'Sprawdź funkcję i dozwolone zmiany.',
  unknown:
    'Nie udało się potwierdzić, czy te zmiany zostały dozwolone. Sprawdź ponownie, aby potwierdzić to samo żądanie.',
  storage: 'Nie udało się zachować tego żądania w tej karcie. Odśwież stronę przed ponowną próbą.',
  another: 'Skonfiguruj kolejną funkcję',
};
const pt: Copy = {
  heading: 'Permitir que esta função altere os dados da App',
  intro:
    'Escolha a função, quem a pode utilizar e as alterações que pode fazer. O acesso existente à App continua a aplicar-se.',
  function: 'Função',
  who: 'Quem a pode utilizar',
  owner: 'Só o proprietário da App',
  members: 'Pessoas com acesso a esta App',
  collection: 'Dados guardados',
  effects: 'Alterações permitidas',
  insert: 'Adicionar linhas',
  update: 'Editar linhas',
  delete: 'Remover linhas',
  allow: 'Permitir alterações',
  checking: 'A verificar…',
  check: 'Verificar novamente',
  allowed: 'Estas alterações são permitidas.',
  denied: 'Não pode permitir estas alterações.',
  changed: 'A App mudou. Volte a abrir a versão atual antes de alterar o acesso.',
  invalid: 'Reveja a função e as alterações permitidas.',
  unknown:
    'Não foi possível confirmar se estas alterações foram permitidas. Verifique novamente para confirmar o mesmo pedido.',
  storage:
    'Não foi possível manter este pedido neste separador. Recarregue antes de tentar novamente.',
  another: 'Configurar outra função',
};
const ro: Copy = {
  heading: 'Permiteți acestei funcții să modifice datele aplicației',
  intro:
    'Alegeți funcția, cine o poate folosi și modificările pe care le poate face. Accesul existent la aplicație rămâne valabil.',
  function: 'Funcție',
  who: 'Cine o poate folosi',
  owner: 'Doar proprietarul aplicației',
  members: 'Persoanele cu acces la această aplicație',
  collection: 'Date salvate',
  effects: 'Modificări permise',
  insert: 'Adăugare de rânduri',
  update: 'Editare de rânduri',
  delete: 'Eliminare de rânduri',
  allow: 'Permiteți modificările',
  checking: 'Se verifică…',
  check: 'Verifică din nou',
  allowed: 'Aceste modificări sunt permise.',
  denied: 'Nu puteți permite aceste modificări.',
  changed:
    'Aplicația s-a schimbat. Redeschideți versiunea ei actuală înainte de a modifica accesul.',
  invalid: 'Verificați funcția și modificările permise.',
  unknown:
    'Nu am putut confirma dacă aceste modificări au fost permise. Verificați din nou pentru a confirma aceeași solicitare.',
  storage:
    'Nu am putut păstra această solicitare în această filă. Reîncărcați pagina înainte de a încerca din nou.',
  another: 'Configurați altă funcție',
};
const sk: Copy = {
  heading: 'Povoliť tejto funkcii meniť údaje aplikácie',
  intro:
    'Vyberte funkciu, kto ju môže používať a aké zmeny môže robiť. Existujúci prístup k aplikácii naďalej platí.',
  function: 'Funkcia',
  who: 'Kto ju môže používať',
  owner: 'Len vlastník aplikácie',
  members: 'Ľudia s prístupom k tejto aplikácii',
  collection: 'Uložené údaje',
  effects: 'Povolené zmeny',
  insert: 'Pridávať riadky',
  update: 'Upravovať riadky',
  delete: 'Odstraňovať riadky',
  allow: 'Povoliť zmeny',
  checking: 'Kontroluje sa…',
  check: 'Skontrolovať znova',
  allowed: 'Tieto zmeny sú povolené.',
  denied: 'Tieto zmeny nemôžete povoliť.',
  changed: 'Aplikácia sa zmenila. Pred zmenou prístupu znova otvorte jej aktuálnu verziu.',
  invalid: 'Skontrolujte funkciu a povolené zmeny.',
  unknown:
    'Nepodarilo sa overiť, či boli tieto zmeny povolené. Skontrolujte to znova a potvrďte rovnakú požiadavku.',
  storage:
    'Túto požiadavku sa nepodarilo uchovať na tejto karte. Pred ďalším pokusom stránku znova načítajte.',
  another: 'Nastaviť ďalšiu funkciu',
};
const sl: Copy = {
  heading: 'Dovoli tej funkciji spreminjati podatke aplikacije',
  intro:
    'Izberite funkcijo, kdo jo sme uporabljati in katere spremembe sme narediti. Obstoječi dostop do aplikacije še vedno velja.',
  function: 'Funkcija',
  who: 'Kdo jo sme uporabljati',
  owner: 'Samo lastnik aplikacije',
  members: 'Osebe z dostopom do te aplikacije',
  collection: 'Shranjeni podatki',
  effects: 'Dovoljene spremembe',
  insert: 'Dodajanje vrstic',
  update: 'Urejanje vrstic',
  delete: 'Odstranjevanje vrstic',
  allow: 'Dovoli spremembe',
  checking: 'Preverjanje …',
  check: 'Preveri znova',
  allowed: 'Te spremembe so dovoljene.',
  denied: 'Teh sprememb ne morete dovoliti.',
  changed:
    'Aplikacija se je spremenila. Pred spremembo dostopa znova odprite njeno trenutno različico.',
  invalid: 'Preglejte funkcijo in dovoljene spremembe.',
  unknown:
    'Nismo mogli potrditi, ali so bile te spremembe dovoljene. Preverite znova, da potrdite isto zahtevo.',
  storage:
    'Te zahteve nismo mogli ohraniti v tem zavihku. Pred ponovnim poskusom znova naložite stran.',
  another: 'Nastavi še eno funkcijo',
};
const sv: Copy = {
  heading: 'Tillåt den här funktionen att ändra appdata',
  intro:
    'Välj funktionen, vem som får använda den och vilka ändringar den får göra. Befintlig åtkomst till appen gäller fortfarande.',
  function: 'Funktion',
  who: 'Vem som får använda den',
  owner: 'Bara appens ägare',
  members: 'Personer med åtkomst till den här appen',
  collection: 'Sparad data',
  effects: 'Tillåtna ändringar',
  insert: 'Lägga till rader',
  update: 'Redigera rader',
  delete: 'Ta bort rader',
  allow: 'Tillåt ändringar',
  checking: 'Kontrollerar…',
  check: 'Kontrollera igen',
  allowed: 'Ändringarna är tillåtna.',
  denied: 'Du kan inte tillåta de här ändringarna.',
  changed: 'Appen har ändrats. Öppna dess aktuella version igen innan du ändrar åtkomsten.',
  invalid: 'Granska funktionen och de tillåtna ändringarna.',
  unknown:
    'Vi kunde inte bekräfta om ändringarna tilläts. Kontrollera igen för att bekräfta samma begäran.',
  storage: 'Vi kunde inte behålla begäran i den här fliken. Ladda om innan du försöker igen.',
  another: 'Konfigurera en funktion till',
};
export const appFunctionWriteSetupCopy: NamespaceCatalog<AppFunctionWriteSetupCopyKey> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default appFunctionWriteSetupCopy;
